// services/usageFormat.js — the words under a reply and on the month strip.
//
// Numbers a person can read at a glance, and never a dollar figure: the
// share of the month is the unit, as Claude shows it.
import { STATIC_MENU, MODEL_LABELS, resolveModelId, labelFor } from './modelMenu';

// The name of the model that wrote a reply. Looked up as itself first: an
// older reply was written by GPT-4o mini, and naming it by what that id means
// today (Auto) would misreport it. Only an id that never answered anything
// (a retired alias) is read forward.
export const modelLabel = (id, menu = STATIC_MENU) => {
  if (!id) return '';
  if ((menu || []).some(m => m.id === id) || MODEL_LABELS[id]) return labelFor(id, menu);
  return labelFor(resolveModelId(id), menu);
};

/** "Auto · Claude Sonnet 5.5" when Auto chose; the model's name when picked by hand. */
export const routeLabel = (route, menu) => {
  if (!route) return '';
  const name = route.modelLabel || modelLabel(route.model, menu);
  if (route.auto) return name && name !== 'Auto' ? `Auto · ${name}` : 'Auto';
  return name || '';
};

/** The route a turn took: its `route` event, or (a reopened chat) its receipt. */
export const routeOf = (msg) => {
  if (msg?.route) return msg.route;
  const u = msg?.usage;
  return u && typeof u.auto === 'boolean' && u.model ? { auto: u.auto, model: u.model } : null;
};

/** 21000 -> "21k", 1500 -> "1.5k", 800 -> "800". */
export const compact = (n) => {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  const k = v / 1000;
  return `${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`;
};

/** "0.3% of your month", "less than 0.1% of your month". */
export const turnShare = (pct) => {
  const v = Number(pct) || 0;
  if (v <= 0) return '';
  if (v < 0.1) return 'less than 0.1% of your month';
  return `${v}% of your month`;
};

/**
 * The receipt under one reply. `withModel: false` when the route line
 * (routeLabel) already names the model, so it is not said twice.
 */
export const replyFooter = (usage, menu, { admin = true, withModel = true } = {}) => {
  if (!usage || !usage.calls) return '';
  // Token counts are the admin's (founder, 2026-09-27). Everyone else sees at
  // most the share of their month, and only on a metered plan.
  if (!admin) return turnShare(usage.turnPct) || '';
  const fellBack = Boolean(usage.models && usage.models.length > 1);
  const parts = [];
  if (withModel) parts.push(`${modelLabel(usage.model, menu)}${fellBack ? ' (after a fallback)' : ''}`);
  else if (fellBack) parts.push(`${modelLabel(usage.model, menu)} after a fallback`);
  parts.push(`${compact(usage.input_tokens)} in · ${compact(usage.output_tokens)} out`);
  if (usage.calls > 1) parts.push(`${usage.calls} calls`);
  const share = turnShare(usage.turnPct);
  if (share) parts.push(share);
  return parts.join(' · ');
};

/**
 * The one quiet line under a finished reply: which model answered (for
 * everyone, chat-2: "Auto · Claude Sonnet 5.5"), then the receipt (tokens for
 * the admin, the share of the month for a metered plan).
 */
export const turnFooter = (msg, menu, { admin = false } = {}) => {
  const route = routeLabel(routeOf(msg), menu);
  const receipt = replyFooter(msg?.usage, menu, { admin, withModel: !route });
  return [route, receipt].filter(Boolean).join(' · ');
};

/** When the month's allowance starts again: "Oct 1", or '' when unknown. */
export const resetsOn = (summary) => {
  const reset = summary?.resetsAt ? new Date(summary.resetsAt) : null;
  return reset && !Number.isNaN(reset.getTime())
    ? reset.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : '';
};

/** The month strip by the picker: "12% of this month · resets Oct 1". */
export const monthStrip = (summary) => {
  if (!summary) return '';
  if (summary.unlimited) return 'No monthly ceiling on this plan';
  const when = resetsOn(summary);
  return `${summary.pct || 0}% of this month${when ? ` · resets ${when}` : ''}`;
};

/** Whether this month's AI is used up: the server's own word (`over`), true
 *  only while the limit is enforced and the budget is spent, which is exactly
 *  when the AI refuses. 100% of a month nothing checks is not used up: the
 *  chat still answers, and saying otherwise was the flaw the founder hit
 *  (2026-09-28). A plan with no ceiling never is. */
export const aiUsedUp = (summary) => Boolean(summary && !summary.unlimited && summary.over === true);
